import React, { useEffect, useRef, useState } from "react";

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect fill='%23334155' width='100' height='100'/%3E%3C/svg%3E";

export default function OptimizedImage({
  src,
  alt,
  className,
  priority = false,
  rootMargin = "200px 0px",
  onClick,
  style,
  decoding = "async",
  ...rest
}) {
  const [isVisible, setIsVisible] = useState(priority);
  const [isFadedIn, setIsFadedIn] = useState(priority); // Set to true for priority images
  const imageRef = useRef(null);

  // Reset fade state when src changes
  useEffect(() => {
    setIsFadedIn(priority); // For priority images, show immediately
    // Reset visibility state when src changes to allow IntersectionObserver to re-trigger
    if (!priority) {
      setIsVisible(false);
    }
  }, [src, priority]);

  // IntersectionObserver — only for non-priority images
  useEffect(() => {
    if (priority) {
      setIsVisible(true);
      return;
    }

    const imageNode = imageRef.current;
    if (!imageNode) return;

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(imageNode);
          }
        });
      },
      { rootMargin, threshold: 0.01 }
    );

    observer.observe(imageNode);
    
    // Fallback: Show image after 500ms even if not intersecting
    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, 500);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [priority, rootMargin, src]);

  return (
    <img
      ref={imageRef}
      // Set the real src directly when visible — no hidden Image() preloader double-fetch
      src={isVisible ? src : PLACEHOLDER_IMAGE}
      alt={isVisible ? alt : ""}
      className={className}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding={decoding}
      onClick={onClick}
      onLoad={() => setIsFadedIn(true)}
      style={{
        ...style,
        opacity: isFadedIn ? 1 : 0,
        transition: "opacity 200ms ease",
      }}
      {...rest}
    />
  );
}
